import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { settingsApi } from "@/integrations/mysql/client";
import { useToast } from "@/hooks/use-toast";
import type { SiteSetting } from "@/integrations/mysql/client";

export const PAGE_VISIBILITY_KEYS = {
  blog:          "page_blog_visible",
  servicos:      "page_servicos_visible",
  depoimentos:   "page_depoimentos_visible",
  jornada:       "page_jornada_visible",
  instagram:     "page_instagram_visible",
  nossaHistoria: "page_nossa_historia_visible",
  faq:           "page_faq_visible",
  imprensa:      "page_imprensa_visible",
} as const;

export type PageKey = keyof typeof PAGE_VISIBILITY_KEYS;

export function usePageVisibility() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const { data: settings = [] } = useQuery({
    queryKey: ["site-settings"],
    queryFn: settingsApi.list,
    staleTime: 30_000,
  });

  const mutation = useMutation({
    mutationFn: ({ key, visible }: { key: string; visible: boolean }) =>
      settingsApi.update(key, visible ? "true" : "false"),
    onMutate: async ({ key, visible }) => {
      await queryClient.cancelQueries({ queryKey: ["site-settings"] });
      const previousSettings = queryClient.getQueryData<SiteSetting[]>(["site-settings"]);
      const value = visible ? "true" : "false";

      queryClient.setQueryData<SiteSetting[]>(["site-settings"], (current = []) => {
        const exists = current.some((setting) => setting.key === key);
        return exists
          ? current.map((setting) => setting.key === key
              ? { ...setting, value, is_public: true }
              : setting)
          : [...current, { key, value, description: null, is_public: true }];
      });

      return { previousSettings, hadSettings: previousSettings !== undefined };
    },
    onError: (error, _variables, context) => {
      if (context?.hadSettings) {
        queryClient.setQueryData(["site-settings"], context.previousSettings);
      } else {
        queryClient.setQueryData<SiteSetting[]>(["site-settings"], []);
      }
      queryClient.invalidateQueries({ queryKey: ["site-settings"] });
      toast({
        title: "Não foi possível atualizar a visibilidade",
        description: error instanceof Error ? error.message : "Verifique sua conexão e tente novamente.",
        variant: "destructive",
      });
    },
    onSuccess: (updatedSetting) => {
      queryClient.setQueryData<SiteSetting[]>(["site-settings"], (current = []) => {
        const exists = current.some((setting) => setting.key === updatedSetting.key);
        return exists
          ? current.map((setting) => setting.key === updatedSetting.key ? updatedSetting : setting)
          : [...current, updatedSetting];
      });
      queryClient.invalidateQueries({ queryKey: ["site-settings"] });
      toast({ title: "Visibilidade atualizada" });
    },
  });

  const isVisible = (key: string): boolean => {
    const s = settings.find((s) => s.key === key);
    // default true when setting doesn't exist yet
    return s ? s.value !== "false" : true;
  };

  const setVisible = (key: string, visible: boolean) =>
    mutation.mutate({ key, visible });

  return { isVisible, setVisible, isUpdating: mutation.isPending };
}
